import type { LinkSearchListItemData } from '@atlaskit/link-picker/types';
import type { ActivityItem, Container } from '@atlassian/recent-work-client/query';

import { getIconFromContentType, getIconFromIconUrl } from './ui';

export const mapActivityClientResultToLinkSearchItemData = ({
	object,
	containers,
	timestamp,
}: ActivityItem): LinkSearchListItemData => {
	let iconUrl = object.icon?.value || '';
	if (object.icon?.type === 'EMOJI') {
		iconUrl = object.icon.fallback || '';
	}

	const iconData =
		object.product === 'confluence'
			? getIconFromContentType(
					object.product + '.' + object.type.toLowerCase(),
					object.subtype?.toLowerCase(),
				)
			: getIconFromIconUrl(iconUrl);

	return {
		objectId: object.id,
		name: object.name || '',
		container: getContainerName(containers),
		url: object.url,
		...iconData,
		lastViewedDate: timestamp ? new Date(timestamp) : undefined,
		prefetch: true,
		meta: {
			source: 'recent-work',
		},
	};
};

const getContainerName = (containers: Array<Container>): string => {
	for (let i = containers.length - 1; i >= 0; --i) {
		const containerName = containers[i].name;
		if (containerName) {
			return containerName;
		}
	}
	return '';
};

/** No results returned for townsquare (Atlas) if `platform` is used in filter
 * https://atlassian.slack.com/archives/CGZMUTRUL/p1684729057252109?thread_ts=1684727786.451419&cid=CGZMUTRUL
 */

/**
 * Generate the ARI for particular product to send to the recent items API.
 */
function generateProductSpecificAri(product: string, cloudId: string): string {
	if (product === 'townsquare') {
		/** No results returned for townsquare (Atlas) if `platform` is used in filter
		 * https://atlassian.slack.com/archives/CGZMUTRUL/p1684729057252109?thread_ts=1684727786.451419&cid=CGZMUTRUL
		 */
		return `ari:cloud:townsquare::site/${cloudId}`;
	}
	return `ari:cloud:platform::site/${cloudId}`;
}

/**
 * Generate an array of ARI's for a list of products. Generally there is a single product, so the output will be a single
 * ARI string.
 * If multiple arrays have the same ARI they will be deduplicated
 */
export function getArisForProducts(products: string[] | undefined, cloudId: string): string[] {
	const defaultAri = `ari:cloud:platform::site/${cloudId}`;
	if (products === undefined) {
		return [defaultAri];
	}
	const allAris: string[] = products.map((product) => generateProductSpecificAri(product, cloudId));
	// Deduplicate
	return allAris.filter((value, index) => allAris.indexOf(value) === index);
}
